import React, { useState, useEffect, useRef } from "react";
import { Modal, message, Select, Spin } from "antd";
import moment from "moment";
import {
  MdKeyboardArrowRight,
  MdOutlineKeyboardArrowLeft,
} from "react-icons/md";
import { generateDates, changeIntoString } from "../utils/dates";
import { useSelector, useDispatch } from "react-redux";
import PatientService from "../Services/PatientService";
import UserService from "../Services/UserService";
import enrollmentServices from "../Services/enrollmentServices";
import { addMinutesToTime, convertUTCDate } from "../utils/convertionFunc";
import { patientPayment } from "../utils/Appointments";
import dayjs from "dayjs";

import { IoAlertCircleOutline } from "react-icons/io5";
const CURRENCYCODE = process.env.REACT_APP_CURRENCYCODE || "";

const { Option } = Select;

const CallDoctorModal = ({ visible, onCancel }) => {
  const dispatch = useDispatch();
  const { patientData } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [fetchingFam, setFetchingFam] = useState(false);
  const [famList, setFamList] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [consCode, setConsCode] = useState("");
  const [consultationReasonList, setConsultationReasonList] = useState([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    reason: "",
    date: "",
    paymentOption: "Pay Now",
  });
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [dates, setDates] = useState([]);
  const [lastDate, setLastDate] = useState(new Date());
  const containerRef = useRef(null);
  const CURRENCYCODE = process.env.REACT_APP_CURRENCYCODE || "";
  useEffect(() => {
    const datesArr = generateDates(new Date(), 30);
    setDates(datesArr);
    if (datesArr && datesArr.length !== 0) {
      setFormData((prev) => ({ ...prev, date: datesArr[0].fullDate }));
    }
  }, []);

  useEffect(() => {
    if (patientData?.patientId) {
      getFamilyList();
      setSelectedPatientId(patientData.patientId);
      setFormData((prev) => ({
        ...prev,
        name: patientData.firstName,
        mobile: patientData.mobile,
      }));
    }
  }, [patientData]);

  const getFamilyList = async () => {
    if (patientData?.parentPatientId) return;
    setFetchingFam(true);
    const payload = { parentPatientId: patientData?.patientId };
    try {
      const response = await PatientService.getPatient(payload);
      setFamList(response.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setFetchingFam(false);
    }
  };

  const handlePatientSelect = (value) => {
    setSelectedPatientId(value);
    const selected =
      value === patientData.patientId
        ? patientData
        : famList.find((f) => f.patientId === value);

    if (selected) {
      setFormData((prev) => ({
        ...prev,
        name: selected.firstName,
        mobile: selected.mobile,
      }));
    }
  };

  const handleConsSelectChange = (reason) => {
    setFormData((prev) => ({
      ...prev,
      reason: reason?.chiefComplaint ? reason?.chiefComplaint : "",
    }));
    setConsCode(reason?.complaintCode ? reason?.complaintCode : "");
    setConsultationReasonList([]);
  };

  const handleConsReason = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, reason: value }));
    if (value && value.length >= 3) {
      const payload = { chiefComplaint: value };
      fetchConsReasons(payload);
    } else {
      setConsultationReasonList([]);
    }
  };

  const fetchConsReasons = async (filterPayload) => {
    const payload = { chiefComplaint: "", status: 1 };
    const finalPayload = filterPayload
      ? { ...payload, ...filterPayload }
      : payload;
    try {
      const response = await PatientService.AllConsReasons(finalPayload);
      setConsultationReasonList(response.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleScroll = () => {
    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
    if (scrollLeft + clientWidth >= scrollWidth - 5) {
      loadMoreDates();
    }
  };

  const loadMoreDates = () => {
    const newLastDate = new Date(lastDate);
    newLastDate.setDate(lastDate.getDate() + 30);
    const newDates = generateDates(newLastDate, 30);
    setDates((prevDates) => [...prevDates, ...newDates]);
    setLastDate(newLastDate);
  };

  const scrollLeft = () => {
    containerRef.current.scrollBy({ left: -100, behavior: "smooth" });
  };

  const scrollRight = () => {
    containerRef.current.scrollBy({ left: 100, behavior: "smooth" });
  };

  const handleSelectDate = (dateObj) => {
    setFormData((prev) => ({ ...prev, date: dateObj.fullDate }));
    setSelectedSlot(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Generate slots from 6 AM to 6 PM (every 10 mins)
  const generateSlots = () => {
    const morning = [];
    const afternoon = [];

    let start = moment("06:00 AM", "hh:mm A");
    const noon = moment("12:00 PM", "hh:mm A");
    const end = moment("06:00 PM", "hh:mm A");

    while (start.isBefore(end)) {
      const timeStr = start.format("hh:mm A");
      if (start.isBefore(noon)) {
        morning.push(timeStr);
      } else {
        afternoon.push(timeStr);
      }
      start.add(10, "minutes");
    }
    afternoon.push(end.format("hh:mm A"));

    return { morning, afternoon };
  };

  const { morning, afternoon } = generateSlots();

  // Filter out past slots when today is selected
  const isToday = dayjs(formData.date).isSame(dayjs(), "day");
  const isPastSlot = (slotStr) => {
    if (!isToday) return false;
    return dayjs(slotStr, "hh:mm A").isBefore(dayjs());
  };
  const filteredMorning = morning.filter((s) => !isPastSlot(s));
  const filteredAfternoon = afternoon.filter((s) => !isPastSlot(s));

  const handlePayLaterConfirm = () => {
    setIsConfirmModalOpen(false);
    handleSubmit(null, "Cash", true);
  };

  const handleSubmit = async (
    e,
    paymentType = "Digital",
    isConfirmed = false,
  ) => {
    if (e) e.preventDefault();

    if (!formData.name) return message.error("Please enter name");
    if (!formData.mobile || !/^[0-9]{10}$/.test(formData.mobile)) {
      return message.error("Please enter a valid 10-digit mobile number");
    }
    if (!formData.date) return message.error("Please select a date");
    if (!selectedSlot) return message.error("Please select a time slot");

    if (paymentType === "Cash" && !isConfirmed) {
      setIsConfirmModalOpen(true);
      return;
    }

    setLoading(true);
    const BOOKING_CONSTANTS =
      process.env.REACT_APP_ENVIRONMENT === "dev"
        ? {
            userId: "DZU3191271",
            account_Id: "DZA3251122",
            clinic_Id: "DZC6141156",
          }
        : {
            userId: "DZU6631021",
            account_Id: "DZA8031009",
            clinic_Id: "DZC4851009",
          };

    const SIGNUP_CONSTANTS = {
      account_Id: "77778888999",
      clinic_Id: "C555566667777",
      userId: "DOXZONADMIN",
      userName: null,
    };

    try {
      let currentPatientId = selectedPatientId;

      // New Patient Registration if not logged in
      if (!currentPatientId) {
        const nameParts = formData.name.trim().split(/\s+/);
        const firstName = nameParts[0];
        const lastName =
          nameParts.length > 1 ? nameParts.slice(1).join(" ") : ".";

        const signupPayload = {
          account_Id: SIGNUP_CONSTANTS.account_Id,
          account_Name: null,
          address1: null,
          address2: null,
          bloodGroup: null,
          city: null,
          clinic_Id: SIGNUP_CONSTANTS.clinic_Id,
          clinic_Name: null,
          country: null,
          createdBy: SIGNUP_CONSTANTS.userId,
          createdDate: convertUTCDate(new Date(), "YYYY-MM-DD HH:mm:ss"),
          dob: "",
          email: "",
          firstName: firstName,
          gender: "",
          lastName: lastName,
          loginId: null,
          loginIdAvailableYN: null,
          maritalStatus: null,
          meetingLink: null,
          mobile: formData.mobile,
          modifiedBy: SIGNUP_CONSTANTS.userId,
          modifiedDate: convertUTCDate(new Date(), "YYYY-MM-DD HH:mm:ss"),
          parentPatientId: null,
          parentPatientName: null,
          patientId: null,
          patientRegFee: 0,
          patientRegFeeYN: "N",
          photo: null,
          photoName: null,
          photoType: null,
          pinCode: null,
          relationCode: null,
          relationDescription: null,
          salutation: null,
          state: null,
          status: 1,
          userId: SIGNUP_CONSTANTS.userId,
          userName: SIGNUP_CONSTANTS.userName,
          sourceChannel: "Website",
        };

        const signupRes = await UserService.addPatient(signupPayload);
        if (signupRes.data && signupRes.data.Patientid) {
          currentPatientId = signupRes.data.Patientid;
        } else {
          throw new Error("Patient registration failed");
        }
      }

      const payload = {
        consultationReason: formData.reason,
        complaintCode: consCode ? consCode : "",
        createdBy: BOOKING_CONSTANTS.userId,
        createdDate: convertUTCDate(new Date(), "YYYY-MM-DD HH:mm:ss"),
        account_Id: BOOKING_CONSTANTS.account_Id,
        clinic_Id: BOOKING_CONSTANTS.clinic_Id,
        firstName: formData.name,
        specialityCode: BOOKING_CONSTANTS.specialityCode,
        mobile: formData.mobile,
        modifiedBy: BOOKING_CONSTANTS.userId,
        modifiedDate: convertUTCDate(new Date(), "YYYY-MM-DD HH:mm:ss"),
        patientId: currentPatientId,
        paymentConsultation: "AC",
        paymentType: paymentType === "Digital" ? "Digital" : "PayLater",
        appointmentDate: dayjs(formData.date).format("MM/DD/YYYY"),
        startTime: dayjs(selectedSlot, "hh:mm A").format("hh:mm A"),
        endTime: addMinutesToTime(
          dayjs(selectedSlot, "hh:mm A").format("HH:mm"),
          15,
        ),
        serviceName: "Audio Consultation",
        serviceCode: "APPT_AUDIO",
        consultationFee: 50,
        totalAmount: 50,
        userId: BOOKING_CONSTANTS.userId,
        patientType: "E",
        patientRegFeeYN: "N",
        status: paymentType === "Digital" ? 0 : 1,
        paymentReceived: "N",
      };

      payload.endTime = dayjs(payload.endTime, "HH:mm").format("hh:mm A");

      const doctorObj = {
        userId: BOOKING_CONSTANTS.userId,
        account_Id: BOOKING_CONSTANTS.account_Id,
        clinic_Id: BOOKING_CONSTANTS.clinic_Id,
      };

      if (paymentType === "Digital") {
        await patientPayment(payload, doctorObj);
        if (CURRENCYCODE !== "INR") {
          message.success("Doctor call scheduled successfully!");
          setLoading(false);
          onCancel();
        }
      } else {
        await enrollmentServices.Invite(payload);
        message.success("Doctor call scheduled successfully!");
        setLoading(false);
        onCancel();
      }
    } catch (error) {
      console.error(error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong. Please try again.";
      message.error(errorMsg);
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        title={
          <div className="flex items-center gap-2 text-[#189FAA]">
            <span className="text-2xl">🩺</span>
            <span className="text-xl font-bold font-['Inter']">
              Schedule a Doctor Call for prescription
            </span>
            <span className="text-xl font-bold font-['Inter'] text-[#189FAA]">{`(${CURRENCYCODE} 50)`}</span>
          </div>
        }
        open={visible}
        onCancel={onCancel}
        footer={null}
        centered
        width={800}
        styles={{
          content: {
            borderRadius: "24px",
            padding: "24px",
          },
          header: {
            borderBottom: "none",
            marginBottom: "16px",
          },
        }}
      >
        <div className="mt-4">
          <p className="text-gray-500 mb-6 text-sm">
            Please fill in the details below and our doctor will connect with
            you at your chosen time.
          </p>

          <form className="space-y-6">
            {patientData ? (
              <div className="relative w-full">
                <span className="absolute -top-2 text-xs font-semibold z-10 left-3 bg-white px-1 text-gray-700">
                  Select Patient <span style={{ color: "#FF000F" }}>*</span>
                </span>
                <Select
                  className="w-full text-xs font-medium font-sans text-[#232323] h-11"
                  value={selectedPatientId}
                  onChange={handlePatientSelect}
                  loading={fetchingFam}
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: "#F2FFFB",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.375rem",
                    }),
                  }}
                >
                  <Option value={patientData.patientId}>
                    {patientData.firstName} (Self)
                  </Option>
                  {famList.map((fam) => (
                    <Option key={fam.patientId} value={fam.patientId}>
                      {fam.firstName}
                    </Option>
                  ))}
                </Select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-6">
                {/* Name Field */}
                <div className="relative w-full">
                  <span className="absolute -top-2 text-xs font-semibold z-10 left-3 bg-white px-1 text-gray-700">
                    Full Name <span style={{ color: "#FF000F" }}>*</span>
                  </span>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none text-xs font-medium font-sans text-[#232323] focus:border-[#189FAA] bg-[#F2FFFB] h-11"
                    required
                  />
                </div>

                {/* Mobile Field */}
                <div className="relative w-full">
                  <span className="absolute -top-2 text-xs font-semibold z-10 left-3 bg-white px-1 text-gray-700">
                    Mobile Number <span style={{ color: "#FF000F" }}>*</span>
                  </span>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="10-digit number"
                    maxLength={10}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none text-xs font-medium font-sans text-[#232323] focus:border-[#189FAA] bg-[#F2FFFB] h-11"
                    required
                  />
                </div>
              </div>
            )}

            {/* Reason Field */}
            <div className="relative w-full">
              <span className="absolute -top-2 text-xs font-semibold z-10 left-3 bg-white px-1 text-gray-700">
                Reason
              </span>
              <input
                type="text"
                name="reason"
                value={formData.reason}
                onChange={handleConsReason}
                placeholder="Reason for consultation"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none text-xs font-medium font-sans text-[#232323] focus:border-[#189FAA] bg-[#F2FFFB] h-11"
              />
              {consultationReasonList.length > 0 && (
                <ul
                  onMouseLeave={() => setConsultationReasonList([])}
                  className="absolute w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-40 overflow-y-auto z-[100] transition-all"
                >
                  {consultationReasonList.map((reason, index) => (
                    <li
                      key={index}
                      onClick={() => handleConsSelectChange(reason)}
                      className="px-4 py-2 text-xs font-medium text-gray-700 hover:bg-[#189FAA10] hover:text-[#189FAA] cursor-pointer border-b last:border-0 border-gray-50 list-none"
                    >
                      {reason.chiefComplaint}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Date Slider Selection */}

            <div className="rounded-xl  p-2">
              <div className="flex justify-between items-center gap-2">
                <button
                  type="button"
                  onClick={scrollLeft}
                  className="p-1 text-[#189FAA] hover:bg-[#189FAA10] rounded-full transition-colors"
                >
                  <MdOutlineKeyboardArrowLeft size={24} />
                </button>

                <div
                  ref={containerRef}
                  onScroll={handleScroll}
                  className="flex items-center gap-3 overflow-x-auto no-scrollbar scroll-smooth"
                  style={{ msOverflowStyle: "none", scrollbarWidth: "none" }}
                >
                  {dates.map((dateObj, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectDate(dateObj)}
                      className={`min-w-[70px] p-2 border rounded-xl font-bold text-xs flex flex-col items-center transition-all
                        ${
                          formData.date === dateObj.fullDate
                            ? "bg-[#189FAA] text-white border-[#189FAA] shadow-md scale-105"
                            : "bg-white text-gray-600 border-gray-100 hover:border-[#189FAA30]"
                        }`}
                    >
                      <span className="opacity-80 uppercase text-[10px]">
                        {dateObj.day}
                      </span>
                      <span className="text-base my-0.5">{dateObj.date}</span>
                      <span className="text-[10px]">{dateObj.month}</span>
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={scrollRight}
                  className="p-1 text-[#189FAA] hover:bg-[#189FAA10] rounded-full transition-colors"
                >
                  <MdKeyboardArrowRight size={24} />
                </button>
              </div>
            </div>

            {/* Slot Selection */}
            <div className="flex flex-col gap-1">
              <label className="font-semibold text-gray-700">
                Select Time Slot
              </label>
              <div className="border border-gray-300 rounded-xl p-4 bg-gray-50 max-h-[300px] overflow-y-auto shadow-inner">
                <div className="mb-6">
                  <p className="text-[#189FAA] font-bold text-xs tracking-wider uppercase mb-3 text-center">
                    Morning Slots
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {filteredMorning.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">
                        No morning slots available
                      </p>
                    ) : (
                      filteredMorning.map((s, index) => (
                        <button
                          key={s}
                          type="button"
                          disabled={index === 0}
                          onClick={() => setSelectedSlot(s)}
                          className={`px-3 py-2 text-xs font-semibold border rounded-lg transition-all min-w-[85px] 
                        ${
                          index === 0
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
                            : selectedSlot === s
                              ? "bg-[#189FAA] text-white border-[#189FAA] shadow-md scale-105"
                              : "bg-white text-gray-600 border-gray-200 hover:border-[#189FAA] hover:text-[#189FAA]"
                        }`}
                        >
                          {s}
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-[#189FAA] font-bold text-xs tracking-wider uppercase mb-3 text-center">
                    Afternoon Slots
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {filteredAfternoon.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">
                        No afternoon slots available
                      </p>
                    ) : (
                      filteredAfternoon.map((s, index) => (
                        <button
                          key={s}
                          type="button"
                          disabled={filteredMorning.length === 0 && index === 0}
                          onClick={() => setSelectedSlot(s)}
                          className={`px-3 py-2 text-xs font-semibold border rounded-lg transition-all min-w-[85px] 
                        ${
                          filteredMorning.length === 0 && index === 0
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
                            : selectedSlot === s
                              ? "bg-[#189FAA] text-white border-[#189FAA] shadow-md scale-105"
                              : "bg-white text-gray-600 border-gray-200 hover:border-[#189FAA] hover:text-[#189FAA]"
                        }`}
                        >
                          {s}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={(e) => handleSubmit(e, "Digital")}
                disabled={loading}
                className={`w-full py-4 mt-4 rounded-xl font-bold text-white text-lg transition-all flex justify-center items-center gap-2
              ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-[#189FAA] hover:bg-[#158a94] shadow-lg active:scale-[0.98]"}`}
              >
                {"Pay Now"}
              </button>
              <button
                type="button"
                onClick={(e) => handleSubmit(e, "Cash")}
                disabled={loading}
                className={`w-full py-4 mt-4 rounded-md font-bold text-white text-lg transition-all
              ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-[#189FAA] hover:bg-[#158a94] shadow-lg active:scale-[0.98]"}`}
              >
                {"Pay Later"}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal
        visible={isConfirmModalOpen}
        onCancel={() => setIsConfirmModalOpen(false)}
        footer={null}
        centered
        width={400}
        styles={{
          content: { borderRadius: "16px", padding: "24px" },
        }}
      >
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mb-4">
            <IoAlertCircleOutline className="text-orange-500 text-4xl" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Confirm Booking
          </h3>
          <p className="text-gray-600 mb-8 leading-relaxed">
            You have chosen to use the Pay Later option. Please note that your
            e-prescription will be provided only after the payment is completed.
          </p>
          <div className="flex gap-3 w-full">
            <button
              onClick={() => setIsConfirmModalOpen(false)}
              className="flex-1 py-3 px-4 border border-teal-100 text-teal-600 font-bold rounded-xl hover:bg-teal-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handlePayLaterConfirm}
              className="flex-1 py-3 px-4 bg-[#189FAA] text-white font-bold rounded-xl hover:bg-[#158a94] shadow-lg shadow-teal-100 transition-colors"
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CallDoctorModal;
